import { useMemo } from "react";
import type {
  BookedFlightItineraryWithDepartureTime,
  FlightItinerarySegment,
} from "@b2bportal/air-booking-api";
import { TravelItineraryEnum } from "@b2bportal/air-booking-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { decodeCityName } from "@overrides/utilities";
import dayjs from "dayjs";

export type AirportMapForDisplay = Record<
  string,
  { name?: string; cityName?: string } | undefined
>;

export type TranslateFn = (
  key: string,
  params?: Record<string, string>
) => string;

/** Airport name (Code), or City name Airport (Code) when name is missing */
export const getAirportDisplayLabel = (
  code: string,
  translate: TranslateFn,
  airportMap?: AirportMapForDisplay | null
): string => {
  if (!code) return "";
  const airport = airportMap?.[code];
  if (airport?.name) {
    const name = decodeCityName(airport.name);
    return translate("flightTrips.airportWithCode", {
      name,
      code,
      interpolation: { escapeValue: false },
    });
  }
  if (airport?.cityName) {
    const cityName = decodeCityName(airport.cityName);
    return translate("flightTrips.cityAirportWithCode", {
      cityName,
      code,
      interpolation: { escapeValue: false },
    });
  }
  return code;
};

export interface SeatAssignment {
  passengerName: string;
  seatNumber: string;
  passengerId: string;
}

export interface FlightSeats {
  route: string;
  origin: string;
  destination: string;
  /** Display label for origin: "Airport Name (Code)" or "City Airport (Code)" */
  originDisplay: string;
  /** Display label for destination */
  destinationDisplay: string;
  seats: SeatAssignment[];
}

export interface LayoverSegment {
  departureTime: string;
  arrivalTime: string;
  originCode: string;
  destinationCode: string;
  /** Display label for origin: "Airport Name (Code)" or "City Airport (Code)" */
  originDisplay: string;
  /** Display label for destination */
  destinationDisplay: string;
  airlineCode: string;
  airlineName: string;
  flightNumber: number | null;
  planeInfo?: string;
  layoverDurationMinutes?: number;
}

export interface LayoverSlice {
  direction: "outbound" | "return";
  date: string;
  destination: string;
  /** Display label for slice destination */
  destinationDisplay: string;
  segments: LayoverSegment[];
}

const computeSeatsByDirection = (
  seats: NonNullable<
    BookedFlightItineraryWithDepartureTime["bookedItinerary"]
  >["seats"]["seats"],
  passengers: NonNullable<
    BookedFlightItineraryWithDepartureTime["bookedItinerary"]
  >["passengers"],
  airportMap: AirportMapForDisplay | null | undefined,
  translate: TranslateFn
): {
  outboundSeats: FlightSeats[] | null;
  returnSeats: FlightSeats[] | null;
} => {
  const passengerMap = new Map<string, string>();
  passengers.alone?.forEach((p) => {
    passengerMap.set(p.nameNumber, `${p.person.givenName} ${p.person.surname}`);
  });
  passengers.withLapInfants?.forEach(({ adult }) => {
    passengerMap.set(
      adult.nameNumber,
      `${adult.person.givenName} ${adult.person.surname}`
    );
  });

  const seatsByRoute = new Map<
    string,
    { assignments: SeatAssignment[]; origin: string; destination: string }
  >();

  seats.forEach((seat) => {
    const route = `${seat.origin} to ${seat.destination}`;
    if (!seatsByRoute.has(route)) {
      seatsByRoute.set(route, {
        assignments: [],
        origin: seat.origin,
        destination: seat.destination,
      });
    }
    const routeData = seatsByRoute.get(route)!;
    routeData.assignments.push({
      passengerName: passengerMap.get(seat.passengerId?.value || "") || "",
      seatNumber: seat.displayString || seat.seatId?.value || "",
      passengerId: seat.passengerId?.value || "",
    });
  });

  const outgoingRoutes = new Set(
    seats
      .filter((s) => s.outgoing)
      .map((s) => `${s.origin} to ${s.destination}`)
  );

  const outbound: FlightSeats[] = [];
  const returnFlights: FlightSeats[] = [];
  seatsByRoute.forEach((routeData, route) => {
    const flightSeats: FlightSeats = {
      route,
      origin: routeData.origin,
      destination: routeData.destination,
      originDisplay: getAirportDisplayLabel(
        routeData.origin,
        translate,
        airportMap
      ),
      destinationDisplay: getAirportDisplayLabel(
        routeData.destination,
        translate,
        airportMap
      ),
      seats: routeData.assignments,
    };
    if (outgoingRoutes.has(route)) {
      outbound.push(flightSeats);
    } else {
      returnFlights.push(flightSeats);
    }
  });

  return {
    outboundSeats: outbound.length > 0 ? outbound : null,
    returnSeats: returnFlights.length > 0 ? returnFlights : null,
  };
};

const computeLayoverSlices = (
  itinerary: BookedFlightItineraryWithDepartureTime,
  airportMap: AirportMapForDisplay | null | undefined,
  airlineMap: Record<string, { displayName: string }> | undefined,
  translate: TranslateFn
): LayoverSlice[] => {
  // Hide layover details for multi-city itineraries
  // Multi-city support will be added in a future PR
  const itineraryType =
    itinerary.bookedItinerary?.travelItinerary?.TravelItinerary;
  if (itineraryType === TravelItineraryEnum.MultiTravelItinerary) {
    return [];
  }

  const slices = itinerary.bookedItinerary?.travelItinerary?.slices ?? [];
  const result: LayoverSlice[] = [];

  // Determine trip origin and final destination to identify round-trip vs one-way
  const tripOrigin = slices[0]?.segments?.[0]?.origin?.locationCode ?? "";
  const lastSlice = slices[slices.length - 1];
  const lastSegment = lastSlice?.segments?.[lastSlice.segments.length - 1];
  const tripFinalDestination = lastSegment?.destination?.locationCode ?? "";
  const isRoundTrip = tripOrigin === tripFinalDestination;

  slices.forEach((slice, sliceIndex) => {
    const segments = slice.segments ?? [];
    if (segments.length <= 1) return;

    const firstSegment = segments[0];
    if (!firstSegment) return;

    const lastSeg = segments[segments.length - 1];
    const destination = lastSeg?.destination?.locationCode ?? "";

    // Determine direction based on whether it's a round-trip
    let direction: "outbound" | "return";
    if (isRoundTrip) {
      // For round-trips: first slice is outbound, others are return
      direction = sliceIndex === 0 ? "outbound" : "return";
    } else {
      // For one-way trips: all slices are outbound
      direction = "outbound";
    }

    const layoverSegments: LayoverSegment[] = segments.map(
      (segment: FlightItinerarySegment, segIndex: number) => {
        const nextSegment = segments[segIndex + 1];
        let layoverDurationMinutes: number | undefined;
        if (nextSegment) {
          const arrivalTime = segment.scheduledArrival ?? "";
          const nextDepartureTime = nextSegment.scheduledDeparture ?? "";
          if (arrivalTime && nextDepartureTime) {
            const arrival = dayjs(arrivalTime);
            const nextDeparture = dayjs(nextDepartureTime);

            // Validate dates and ensure positive duration
            if (arrival.isValid() && nextDeparture.isValid()) {
              const duration = nextDeparture.diff(arrival, "minutes");
              // Only set duration if positive (guards against timezone issues)
              if (duration > 0) {
                layoverDurationMinutes = duration;
              }
            }
          }
        }

        const originCode = segment.origin?.locationCode ?? "";
        const destinationCode = segment.destination?.locationCode ?? "";
        const airlineCode =
          segment.marketingAirline?.code ??
          segment.operatingAirline?.code ??
          "";

        return {
          departureTime: segment.scheduledDeparture ?? "",
          arrivalTime: segment.scheduledArrival ?? "",
          originCode,
          destinationCode,
          originDisplay: getAirportDisplayLabel(
            originCode,
            translate,
            airportMap
          ),
          destinationDisplay: getAirportDisplayLabel(
            destinationCode,
            translate,
            airportMap
          ),
          airlineCode,
          airlineName: airlineMap?.[airlineCode]?.displayName ?? airlineCode,
          flightNumber:
            segment.marketingAirline?.flightNumber ??
            segment.operatingAirline?.flightNumber ??
            null,
          planeInfo: segment.equipmentType || undefined,
          layoverDurationMinutes,
        };
      }
    );

    result.push({
      direction,
      date: firstSegment.scheduledDeparture ?? "",
      destination,
      destinationDisplay: getAirportDisplayLabel(
        destination,
        translate,
        airportMap
      ),
      segments: layoverSegments,
    });
  });

  return result;
};

export const useFlightExtrasSection = (
  itinerary: BookedFlightItineraryWithDepartureTime,
  airportMap?: AirportMapForDisplay | null,
  airlineMap?: Record<string, { displayName: string }>
) => {
  const { t: translate } = useI18nContext();
  return useMemo(() => {
    const seats = itinerary.bookedItinerary?.seats?.seats;
    const passengers = itinerary.bookedItinerary?.passengers;

    const { outboundSeats, returnSeats } =
      seats && passengers
        ? computeSeatsByDirection(seats, passengers, airportMap, translate)
        : { outboundSeats: null, returnSeats: null };
    const layoverSlices = computeLayoverSlices(
      itinerary,
      airportMap,
      airlineMap,
      translate
    );

    return {
      outboundSeats,
      returnSeats,
      hasSeats: !!(outboundSeats || returnSeats),
      layoverSlices,
      hasLayovers: layoverSlices.length > 0,
    };
  }, [itinerary, airportMap, airlineMap, translate]);
};
